import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../common/Modal';
import DocumentPreviewModal from '../ui/DocumentPreviewModal';
import {
  listActiveCaseScenariosForInfo,
  type CaseScenarioInfoRow,
} from '../../services/caseScenarioMasters';
import { formatFileSizeLabel } from '../../utils/fileSize';
import { displayClientFileName } from '../../utils/fileDisplayName';

type Props = {
  open: boolean;
  onClose: () => void;
};

function PdfFileIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

export function CaseScenariosInfoPanel({ open, onClose }: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState<CaseScenarioInfoRow[]>([]);
  const [previewScenario, setPreviewScenario] = useState<CaseScenarioInfoRow | null>(null);

  useEffect(() => {
    if (!open) {
      setPreviewScenario(null);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError('');
    void listActiveCaseScenariosForInfo({ signal: controller.signal }).then((res) => {
      if (controller.signal.aborted) return;
      if (!res.success) {
        setError(res.message ?? t('metadata.caseScenarios.infoLoadFailed'));
        setRows([]);
      } else {
        setRows(res.data);
      }
      setLoading(false);
    });

    return () => {
      controller.abort();
    };
  }, [open, t]);

  return (
    <>
      <Modal
        open={open}
        title={t('metadata.caseScenarios.infoPanelTitle')}
        onClose={onClose}
        boxClassName="cn-mbox-case-scenarios-info"
      >
        {loading ? (
          <p style={{ color: 'var(--t2)', margin: 0 }}>{t('common.loading')}</p>
        ) : error ? (
          <p className="si-err" style={{ margin: 0 }}>{error}</p>
        ) : rows.length === 0 ? (
          <p style={{ color: 'var(--t2)', margin: 0 }}>{t('metadata.caseScenarios.infoEmpty')}</p>
        ) : (
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {rows.map((row) => {
              const fileName = displayClientFileName(
                row.document.originalFileName,
                row.document.fileName,
              );
              const fileSize = formatFileSizeLabel(row.document.fileSize);
              return (
                <li key={row.id}>
                  <button
                    type="button"
                    className="mfb"
                    style={{
                      width: '100%',
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      padding: '12px 14px',
                    }}
                    onClick={() => setPreviewScenario(row)}
                  >
                    <span style={{ marginTop: 2, color: 'var(--primary)', flexShrink: 0 }}>
                      <PdfFileIcon />
                    </span>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontWeight: 600 }}>{row.title}</span>
                      {row.description ? (
                        <span style={{ display: 'block', fontSize: 12, color: 'var(--t2)', marginTop: 4 }}>
                          {row.description}
                        </span>
                      ) : null}
                      <span style={{ display: 'block', fontSize: 11, color: 'var(--t2)', marginTop: 4 }}>
                        {fileName}
                        {fileSize ? ` · ${fileSize}` : ''}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Modal>

      <DocumentPreviewModal
        isOpen={Boolean(previewScenario)}
        documentName={previewScenario?.title ?? ''}
        fileName={previewScenario?.document.fileName ?? ''}
        originalFileName={previewScenario?.document.originalFileName ?? ''}
        localFile={null}
        onClose={() => setPreviewScenario(null)}
      />
    </>
  );
}
